import { auth, db } from '../firebase';
import {
  collection, doc, getDoc, getDocs, limit, orderBy, query, setDoc, where,
} from 'firebase/firestore';

const CODE_KEY = 'invite_code';
const CODE_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // no ambiguous 0/O/1/I

function randomCode(len = 6) {
  let out = '';
  for (let i = 0; i < len; i++) out += CODE_CHARS[Math.floor(Math.random() * CODE_CHARS.length)];
  return out;
}

/**
 * `publicProfiles/{uid}` holds only what's safe to show other users
 * (display name, weekly XP, streak, a shareable invite code) — deliberately
 * separate from the private `users/{uid}` doc (email, phone, achievements)
 * so the leaderboard/friends features don't require exposing PII in
 * Firestore security rules.
 */
export const PublicProfileService = {
  /** Cached locally once known; falls back to any existing remote code before minting a new one, so switching devices doesn't fragment a user's invite code. */
  async getOrCreateInviteCode() {
    const cached = localStorage.getItem(CODE_KEY);
    if (cached) return cached;
    const user = auth?.currentUser;
    if (user && db) {
      try {
        const snap = await getDoc(doc(db, 'publicProfiles', user.uid));
        const existing = snap.exists() ? snap.data().inviteCode : null;
        if (existing) {
          localStorage.setItem(CODE_KEY, existing);
          return existing;
        }
      } catch { /* fall through to minting a fresh code */ }
    }
    const code = randomCode();
    localStorage.setItem(CODE_KEY, code);
    return code;
  },

  /** Call after XP/streak change to keep the leaderboard-visible fields current. */
  async sync({ weeklyXp, streak } = {}) {
    const user = auth?.currentUser;
    if (!user || !db) return;
    try {
      const inviteCode = await this.getOrCreateInviteCode();
      const data = {
        displayName: user.displayName || 'Learner',
        inviteCode,
        updatedAt: new Date().toISOString(),
      };
      if (weeklyXp !== undefined) data.weeklyXp = weeklyXp;
      if (streak !== undefined) data.streak = streak;
      await setDoc(doc(db, 'publicProfiles', user.uid), data, { merge: true });
    } catch (e) {
      console.warn('PublicProfileService: sync failed', e);
    }
  },

  async get(uid) {
    if (!db || !uid) return null;
    try {
      const snap = await getDoc(doc(db, 'publicProfiles', uid));
      return snap.exists() ? { uid, ...snap.data() } : null;
    } catch {
      return null;
    }
  },

  async findByInviteCode(code) {
    const normalized = String(code || '').trim().toUpperCase();
    if (!db || !normalized) return null;
    try {
      const snap = await getDocs(
        query(collection(db, 'publicProfiles'), where('inviteCode', '==', normalized), limit(1)),
      );
      if (snap.empty) return null;
      const found = snap.docs[0];
      return { uid: found.id, ...found.data() };
    } catch {
      return null;
    }
  },

  async weeklyLeaderboard(count = 50) {
    if (!db) return [];
    try {
      const snap = await getDocs(
        query(collection(db, 'publicProfiles'), orderBy('weeklyXp', 'desc'), limit(count)),
      );
      return snap.docs.map((d) => ({ uid: d.id, ...d.data() }));
    } catch (e) {
      console.warn('PublicProfileService: weeklyLeaderboard failed', e);
      return [];
    }
  },
};
